/**
 * TracksContainer Component
 * 
 * This component manages the display and tracking of LiveKit video tracks in a fixed 2x2 grid layout.
 * It handles track positioning analytics, status monitoring (online/offline), and provides context
 * for child components to access track position data.
 * 
 * Key Features:
 * - Fixed 2x2 grid layout supporting up to 4 video tracks
 * - Real-time track position analytics with change detection
 * - Automatic offline/online status tracking via MediaStream events
 * - Prevention of false online status reports when tracks are offline
 * - Context provider for track positions accessible by child components
 * 
 * Analytics Flow:
 * 1. Track position changes trigger updateTrackPosition callback
 * 2. MediaStream status is validated before sending online analytics
 * 3. Only changed positions are sent to avoid redundant API calls
 * 4. Offline status is tracked via MediaStream.oninactive events
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useRef,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import TrackWrapper from './TrackWrapper';
import ApiClient from '../../../lib/ApiClient';
import { calculateAspectRatio } from '../utils/aspectRatio';
import { useRecordingContext } from '../RecordingContext';
import { useLogger } from '@ui/shared/components/Logger';
import { Spinner } from '@smarterservices/smarter-ui';
import { BACKGROUND_COLOR, CANVAS_WIDTH, CANVAS_HEIGHT, SPINNER_COLOR, LOGGER_LABEL } from '../utils/constants';

// Initialize API client for sending track analytics to the backend
const apiClient = new ApiClient();

/**
 * Context for sharing track position data across components
 * 
 * @typedef {Object} TrackPositionsContextValue
 * @property {Object} positions - Map of trackSid to position objects {top, left, width, height}
 * @property {Function} updatePosition - Callback to update track position and send analytics
 */
const TrackPositionsContext = createContext({
  positions: {},
  updatePosition: () => {},
});

/**
 * Hook to access track positions from context
 * 
 * @returns {TrackPositionsContextValue} The track positions context containing:
 *   - positions: Object mapping trackSid to position data
 *   - updatePosition: Function to update track position and trigger analytics
 */
export const useTrackPositions = () => useContext(TrackPositionsContext);

/**
 * TracksContainer component renders a fixed 2x2 grid for up to 4 video tracks
 * 
 * This is the main container component that:
 * - Manages track positioning and analytics
 * - Provides context for child components
 * - Handles track status monitoring (online/offline)
 * - Renders tracks in a responsive 2x2 grid layout
 *
 * @param {Object} props - Component props
 * @param {Array<Object>} props.filteredTracks - Array of LiveKit track objects to display.
 *   Each track should have a publication property with trackSid and track reference.
 *   Limited to first 4 tracks for 2x2 grid display.
 * @returns {React.ReactElement} The rendered TracksContainer component
 */
const TracksContainer = ({ filteredTracks }) => {
  /**
   * State to store the current positions of each track
   * Format: { [trackSid]: { top, left, width, height } }
   */
  const [trackPositions, setTrackPositions] = useState({});
  
  /**
   * Ref to store previous analytics data to avoid redundant API calls
   * Only sends analytics when position or status actually changes
   * Format: { [trackSid]: { canvasPosition, status, ...otherTrackData } }
   */
  const prevAnalyticsRef = useRef({});
  
  /**
   * Recording context values required for analytics API calls
   * These identify the specific recording session for backend tracking
   */
  const { installSid, examSid, sessionSid, apiClient } = useRecordingContext();
  
  /**
   * Logger instance for debugging and monitoring track operations
   * Uses the platform logging system with component context
   */
  const logger = useLogger();
  
  // Log component mount/unmount
  useEffect(() => {
    logger.info(LOGGER_LABEL, 'TracksContainer mounted', {
      trackCount: filteredTracks?.length || 0,
      installSid,
      examSid,
      sessionSid,
      hasApiClient: !!apiClient
    });
    
    return () => {
      logger.info(LOGGER_LABEL, 'TracksContainer unmounted', {
        trackedPositions: Object.keys(trackPositions).length
      });
    };
  }, []);
  
  // Log track count changes
  useEffect(() => {
    logger.debug(LOGGER_LABEL, 'Filtered tracks updated', {
      trackCount: filteredTracks?.length || 0,
      displayCount: Math.min(filteredTracks?.length || 0, 4)
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTracks?.length]);
  /**
   * Update position for a specific track and send analytics
   * 
   * This function is called whenever a track's position changes (resize, rotation, etc.)
   * It validates the track is still active before sending online status to prevent
   * false positive online reports after a track has gone offline.
   * 
   * Flow:
   * 1. Sets up MediaStream.oninactive handler for offline detection
   * 2. Calculates track aspect ratio from video dimensions
   * 3. Validates track is active (MediaStream.active && readyState === 'live')
   * 4. Compares with previous data to avoid redundant API calls
   * 5. Sends analytics if position or status changed
   * 6. Updates local position state for context consumers
   *
   * @param {string} trackSid - The unique track identifier from LiveKit
   * @param {Object} trackRef - The LiveKit track reference object containing:
   *   - publication: Track publication with track info and metadata
   *   - participant: Participant information and identity
   * @param {Object} position - The position data from getBoundingClientRect():
   *   - top: Y coordinate relative to viewport
   *   - left: X coordinate relative to viewport  
   *   - width: Element width in pixels
   *   - height: Element height in pixels
   */
  const updateTrackPosition = useCallback(
    async (trackSid, trackRef, position) => {
      // Extract track and mediaStream references for status checking
      const track = trackRef;
      const mediaStream = trackRef?.publication?.track?.mediaStream;

      /**
       * Set up the oninactive handler for the mediaStream
       * This fires when the track goes offline (network issues, device disconnection, etc.)
       * Immediately sends offline status to backend for accurate tracking
       */
      if (mediaStream) {
        /**
         * MediaStream inactive event handler
         * Triggers when the underlying media stream becomes inactive
         * (network disconnection, device removal, etc.)
         */
        mediaStream.oninactive = async () => {
          try {
            // Create offline status payload for this specific track
            const offlineData = {};
            offlineData[trackSid] = {
              status: 'offline',
              changeDate: new Date().toISOString(),
            };
            
            // Log the offline event for debugging
            logger.info(LOGGER_LABEL, 'Track went offline', {
              trackSid,
              participantIdentity: track?.participant?.identity,
              installSid,
              examSid,
              sessionSid
            });
            
            // Validate API client exists
            if (!apiClient) {
              logger.error(LOGGER_LABEL, 'Cannot send offline status: API client not available', {
                trackSid
              });
              return;
            }
            
            // Immediately send offline status to backend
            await apiClient.registerTrack({
              installSid,
              examSid,
              sessionSid,
              payload: offlineData,
            });
            
            logger.debug(LOGGER_LABEL, 'Offline status sent successfully', {
              trackSid
            });
          } catch (error) {
            logger.error(LOGGER_LABEL, 'Failed to send offline status', {
              error,
              errorMessage: error.message,
              trackSid,
              installSid,
              examSid,
              sessionSid
            });
          }
        };
      }

      /**
       * Calculate aspect ratio from track video dimensions
       * Used for analytics and layout optimization
       * Falls back to 'unknown' if dimensions not available
       */
      let aspectRatio = 'unknown';
      if (
        track.publication.trackInfo &&
        track.publication.trackInfo.width &&
        track.publication.trackInfo.height
      ) {
        aspectRatio = calculateAspectRatio(
          track.publication.trackInfo.width,
          track.publication.trackInfo.height
        );
      }

      /**
       * Prepare comprehensive track data payload for analytics
       * Includes participant info, track metadata, position, and status
       */
      const trackData = {};
      trackData[trackSid] = {
        // Participant identification
        participantSid:
          track.participant?.participantInfo?.attributes?.participantSid ||
          null,
        identity: track.participant.identity,
        
        // Track metadata
        source: track.publication.source, // camera, screen, etc.
        kind: track.publication.kind,     // video, audio
        trackInfo: track.publication.trackInfo, // dimensions, codec, etc.
        encodings: track.publication.encodings || [], // quality layers
        
        // Calculated properties
        aspectRatio,
        canvasPosition: position, // current position in viewport
        
        // Status tracking
        status: 'online', // Will only be sent if track is actually active
        changeDate: new Date().toISOString(),
      };

      /**
       * Critical validation: Check if the track is actually active before sending online status
       * 
       * This prevents the bug where position updates would incorrectly mark offline tracks
       * as online again. We validate multiple conditions:
       * - mediaStream exists (not null/undefined)
       * - mediaStream.active is true (stream is not inactive)
       * - track.readyState is 'live' (track is not ended)
       */
      const isTrackActive = mediaStream && 
        mediaStream.active && 
        track.publication?.track?.streamState === 'active';     
      // Early return if track is offline - preserves offline status set by oninactive handler
      if (!isTrackActive) {
        logger.debug(LOGGER_LABEL, 'Skipping position update - track is offline', {
          trackSid,
          participantIdentity: track?.participant?.identity,
          mediaStreamActive: mediaStream?.active,
          trackStreamState: track.publication?.track?.streamState,
        });
        return;
      }
      
      // Validate API client exists before proceeding
      if (!apiClient) {
        logger.error(LOGGER_LABEL, 'Cannot update track position: API client not available', {
          trackSid
        });
        return;
      }

      /**
       * Change detection to avoid redundant API calls
       * Only sends analytics when position or status actually changes
       * Compares current data with previously sent data
       */
      const prevData = prevAnalyticsRef.current[trackSid];
      const hasChanged =
        !prevData || // First time tracking this track
        prevData.canvasPosition?.top !== position.top ||
        prevData.canvasPosition?.left !== position.left ||
        prevData.canvasPosition?.width !== position.width ||
        prevData.canvasPosition?.height !== position.height ||
        prevData.status !== 'online'; // Status changed from offline to online

      /**
       * Send analytics to backend if position or status has changed
       * Includes error handling and state tracking for future comparisons
       */
      if (hasChanged) {
        // Log the analytics event for debugging
        logger.debug(LOGGER_LABEL, 'Sending track position analytics', {
          trackSid,
          participantIdentity: track?.participant?.identity,
          position,
          status: 'online',
          aspectRatio
        });

        try {
          // Send track data to backend analytics API
          await apiClient.registerTrack({
            installSid,
            examSid,
            sessionSid,
            payload: trackData,
          });

          // Store current data for future change detection
          // Only update ref if API call succeeded
          prevAnalyticsRef.current[trackSid] = { ...trackData[trackSid] };
          
          logger.debug(LOGGER_LABEL, 'Track position analytics sent successfully', {
            trackSid
          });

        } catch (error) {
          // Log API errors for monitoring and debugging
          // Do NOT update prevAnalyticsRef so we retry on next position update
          logger.error(LOGGER_LABEL, 'Failed to send track position analytics', {
            error,
            errorMessage: error.message,
            trackSid,
            participantIdentity: track?.participant?.identity,
            position,
            installSid,
            examSid,
            sessionSid
          });
        }
      }

      /**
       * Update local track positions state
       * This provides position data to child components via context
       * Updated regardless of whether analytics were sent
       */
      setTrackPositions((prev) => ({
        ...prev,
        [trackSid]: position,
      }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [installSid, examSid, sessionSid, apiClient] // Dependencies for useCallback
  );

  /**
   * Limit to maximum 4 tracks for 2x2 grid layout
   * Additional tracks beyond 4 are ignored
   */
  const displayTracks = filteredTracks.slice(0, 4);
  
  return (
    /**
     * Provide track positions context to child components
     * Allows TrackWrapper and other components to access position data
     * and trigger position updates
     */
    <TrackPositionsContext.Provider
      value={{ positions: trackPositions, updatePosition: updateTrackPosition }}
    >
      {/* Main container with fixed positioning and 2x2 CSS Grid layout */}
      <div
        style={{
          position: 'fixed', // Fixed positioning for recording overlay
          top: 0,
          left: 0,
          width: `${CANVAS_WIDTH}px`,   // Defined in constants
          height: `${CANVAS_HEIGHT}px`, // Defined in constants
          margin: 0,
          padding: 0,
          overflow: 'hidden',
          display: 'grid',
          gridTemplateRows: '1fr 1fr',    // 2 equal rows
          gridTemplateColumns: '1fr 1fr', // 2 equal columns
          gap: 0,
          background: BACKGROUND_COLOR,
        }}
      >
        {/* Render 4 grid cells (2x2 layout) */}
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              width: `${CANVAS_WIDTH / 2}px`,  // Half canvas width
              height: `${CANVAS_HEIGHT / 2}px`, // Half canvas height
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: BACKGROUND_COLOR,
            }}
          >
            {displayTracks[i] ? (
              /* Render track if available */
              <TrackWrapper
                trackSid={displayTracks[i].publication.trackSid}
                trackRef={displayTracks[i]}
                onPositionUpdate={updateTrackPosition} // Callback for position changes
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 'auto',
                }}
              />
            ) : (
              /* Show loading spinner when track slot is empty */
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: '2rem',
                }}
              >
                <Spinner 
                  size="small" 
                  variant='inverse'
                  themeOverride={{
                    inverseColor: SPINNER_COLOR
                  }} 
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </TrackPositionsContext.Provider>
  );
};

TracksContainer.propTypes = {
  filteredTracks: PropTypes.array.isRequired,
};

export default TracksContainer;
